/*
 * WHAT THE MAP MEANS, for someone seeing it for the first time.
 *
 * The universe is a field of coloured dots, squares and diamonds, some ringed in gold — and nothing
 * on screen said what any of it meant: which colour is which scripture, why some names are squares,
 * what the gold ring is. This is that key. Collapsed by default so it never covers the map, opened
 * with one tap; a real `<details>`, so it is keyboard-reachable and needs no state of its own.
 */
import { useLang } from '../i18n/lang';
import { NO_SCRIPTURE_COLOUR } from '@versesverse/core';

export interface MapLegendProps {
  /** The scriptures and the colour each one's names wear. */
  corpora: { id: string; name: string; colour: string }[];
}

export function MapLegend({ corpora }: MapLegendProps): React.JSX.Element {
  const { t } = useLang();
  return (
    <details className="legend">
      <summary className="legend-summary">{t('legend.open')}</summary>
      <div className="legend-body">
        <h2 className="legend-heading">{t('legend.scriptures')}</h2>
        <ul className="legend-list">
          {corpora.map(corpus => (
            <li key={corpus.id}>
              <span className="legend-dot" style={{ backgroundColor: corpus.colour }} aria-hidden="true" />
              {corpus.name}
            </li>
          ))}
          {/*
            * AND THE COLOUR 75 NAMES WEAR THAT NO SCRIPTURE GAVE THEM.
            *
            * buildNodes colours a node by the text that names it most and falls back to grey when
            * no text names it at all. That is not an absence of meaning — it is two real groups.
            * Figures the scriptures DESCRIBE and never name: the son Abraham was commanded to
            * sacrifice, Pharaoh's wife, al-Khidr, the Queen of Sheba. And figures the hadith name
            * and no scripture does: Abu Bakr, Khaybar, Ashura, the family of Muhammad.
            *
            * Four colours were listed and a fifth was on the map. A reader counting the key
            * against the field would come up one short, on the group likeliest to puzzle them.
            */}
          <li>
            {/* From the renderer's own constant, exactly as the corpus dots take theirs from the
                corpus. A colour duplicated into a stylesheet is a colour that can drift. */}
            <span className="legend-dot" style={{ backgroundColor: NO_SCRIPTURE_COLOUR }} aria-hidden="true" />
            {t('legend.noScripture')}
          </li>
        </ul>

        {/*
          * EACH SHAPE IS A GROUP, AND THESE LABELS USED TO NAME ONE MEMBER OF IT.
          *
          * core/map-model.ts keeps "a deliberately small visual language: three shapes" and puts
          * every kind into one by its nature — a disc for beings, a square for things that sit in
          * the world, a diamond for bodies of many. That was written when the map held people,
          * places and peoples, and the legend named exactly those three.
          *
          * The groups have widened since. A square is now also an object, a language and a
          * SCRIPTURE; a diamond is also an institution, a time and an OBSERVANCE. So the legend
          * was telling a reader that the Torah is a Place and that Ramadan is a People — both
          * kinds added the same week, and the legend is what a reader consults precisely when a
          * mark is unfamiliar.
          *
          * Named by what the group IS now. The exact kind is still in the drawer, which is where
          * map-model says it belongs.
          */}
        <h2 className="legend-heading">{t('legend.shapes')}</h2>
        <ul className="legend-list">
          <li><span className="legend-shape legend-shape--circle" aria-hidden="true" />{t('legend.shapeBeings')}</li>
          <li><span className="legend-shape legend-shape--square" aria-hidden="true" />{t('legend.shapeThings')}</li>
          <li><span className="legend-shape legend-shape--diamond" aria-hidden="true" />{t('legend.shapeBodies')}</li>
        </ul>

        {/*
          * THE SIZE WAS THE ONE THING THIS LEGEND NEVER EXPLAINED.
          *
          * Colour, shape, grey, the gold ring and faintness all had a line. Size — the loudest
          * channel on the field, Moses four times the radius of Darius — had none, so a reader
          * was left to guess, and the obvious guess for a map about shared scripture is that a
          * bigger circle spans more of them. It does not: figureRadius is sqrt(verses naming the
          * figure), with no corpus term anywhere in it.
          *
          * The second clause is not a hedge. Auto-found nodes are drawn on a shorter scale that
          * caps at 9, so Yose, named 388 times, is drawn smaller than Sheba, named 35. Comparing
          * two hand-checked circles is exact — 0 of 81,458 visibly different pairs disagree with
          * the counts — and comparing across that boundary is what the reader is warned off.
          */}
        <h2 className="legend-heading">{t('legend.marks')}</h2>
        <ul className="legend-list">
          <li><span className="legend-size" aria-hidden="true" />{t('legend.size')}</li>
          <li><span className="legend-ring" aria-hidden="true" />{t('legend.gold')}</li>
          <li><span className="legend-shape legend-shape--circle legend-faint" aria-hidden="true" />{t('legend.faint')}</li>
        </ul>
      </div>
    </details>
  );
}
